import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getCart, clearCart } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, CreditCard, Banknote, Ticket, Check, ShoppingBag, Truck, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'carte', label: 'Carte Bancaire', sublabel: 'Paiement à la livraison', icon: CreditCard },
  { value: 'especes', label: 'Espèces', sublabel: 'Paiement à la livraison', icon: Banknote },
  { value: 'ticket-restaurant', label: 'Ticket Restaurant', sublabel: '1 seul ticket par commande', icon: Ticket },
];

const ZONE_FRAIS_LIVRAISON = ['13002', '13007', '13009', '13013'];
const FRAIS_LIVRAISON = 2.50;
const MINIMUM_LIVRAISON_GRATUITE = 20;

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [freeDrink, setFreeDrink] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: 'Marseille',
    paymentMethod: '',
    cashChange: '',
    orderType: '',
    scheduledDate: '',
    scheduledTime: '',
    scheduledMode: '',
  });

  useEffect(() => {
    const currentCart = getCart();
    if (currentCart.length === 0) {
      navigate('/menu');
      toast.error('Votre panier est vide');
      return;
    }
    setCart(currentCart);
    setTotal(calculateTotal(currentCart));

    const savedDrink = localStorage.getItem('casaDelPizzaFreeDrink');
    if (savedDrink) {
      setFreeDrink(JSON.parse(savedDrink));
    }
  }, [navigate]);

  const calculateTotal = (cartItems) => {
    let sum = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    const pizzaCount = cartItems
      .filter(item => item.category && !['boisson', 'salade', 'dessert'].includes(item.category))
      .reduce((acc, item) => acc + item.quantity, 0);
    const fromageCount = cartItems.filter(item => item.name === "Fromage").reduce((acc, item) => acc + item.quantity, 0);

    if (fromageCount >= 3) {
      const sets = Math.floor(fromageCount / 3);
      const fromageItem = cartItems.find(item => item.name === "Fromage");
      if (fromageItem) {
        const normalPrice = fromageItem.price * 3 * sets;
        const promoPrice = 22.50 * sets;
        sum = sum - normalPrice + promoPrice;
      }
    }

    if (pizzaCount >= 10) {
      const nonPizzaSum = cartItems
        .filter(item => item.category && ['boisson', 'salade', 'dessert'].includes(item.category))
        .reduce((acc, item) => acc + (item.price * item.quantity), 0);

      const pizzaItems = cartItems.filter(item => !item.category || !['boisson', 'salade', 'dessert'].includes(item.category));
      let extraPizzaSum = 0;
      let counted = 0;
      for (const item of pizzaItems) {
        if (counted + item.quantity <= 10) {
          counted += item.quantity;
        } else {
          const inDeal = Math.max(0, 10 - counted);
          const extras = item.quantity - inDeal;
          extraPizzaSum += extras * item.price;
          counted += item.quantity;
        }
      }

      sum = 89.90 + extraPizzaSum + nonPizzaSum;
    }

    return sum;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Recalcul frais de livraison
  useEffect(() => {
    const isLivraison = formData.orderType === 'livraison' || (formData.orderType === 'programmer' && formData.scheduledMode === 'livraison');
    if (isLivraison && ZONE_FRAIS_LIVRAISON.includes(formData.codePostal) && total < MINIMUM_LIVRAISON_GRATUITE) {
      setDeliveryFee(FRAIS_LIVRAISON);
    } else {
      setDeliveryFee(0);
    }
  }, [formData.codePostal, formData.orderType, formData.scheduledMode, total]);

  const selectPayment = (method) => {
    setFormData(prev => ({ ...prev, paymentMethod: method }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom || !formData.prenom || !formData.telephone || !formData.paymentMethod || !formData.orderType) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if ((formData.orderType === 'livraison' || (formData.orderType === 'programmer' && formData.scheduledMode === 'livraison')) && !formData.adresse) {
      toast.error('Veuillez indiquer votre adresse de livraison');
      return;
    }

    if (formData.orderType === 'programmer') {
      if (!formData.scheduledDate || !formData.scheduledTime) {
        toast.error('Veuillez choisir la date et l\'heure de votre commande');
        return;
      }
      if (!formData.scheduledMode) {
        toast.error('Veuillez choisir le mode de réception (à emporter ou en livraison)');
        return;
      }
    }

    if (formData.paymentMethod === 'especes' && !formData.cashChange) {
      toast.error("Veuillez indiquer si vous avez l'appoint ou la monnaie nécessaire");
      return;
    }

    setLoading(true);

    try {
      const isDeliveryAddr = formData.orderType === 'livraison' || (formData.orderType === 'programmer' && formData.scheduledMode === 'livraison');
      const orderData = {
        customer: {
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          adresse: isDeliveryAddr ? `${formData.adresse}, ${formData.codePostal} ${formData.ville}` : 'À emporter'
        },
        items: cart.map(item => ({
          name: item.customName || item.name,
          quantity: item.quantity,
          price: item.price,
          chausson: item.chausson || false,
          note: item.note || ''
        })),
        total: total + deliveryFee,
        orderType: formData.orderType,
        scheduledDate: formData.scheduledDate || null,
        scheduledTime: formData.scheduledTime || null,
        scheduledMode: formData.scheduledMode || null,
        deliveryFee: deliveryFee,
        payment: {
          method: formData.paymentMethod,
          cashChange: formData.cashChange || null
        },
        freeDrink: freeDrink?.name || null
      };

      const response = await axios.post(`${BACKEND_URL}/api/orders`, orderData);

      if (response.data.success) {
        clearCart();
        window.dispatchEvent(new Event('storage'));
        toast.success('Commande envoyée avec succès !');
        navigate('/order-confirmation', { state: { orderId: response.data.orderId } });
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error("Erreur lors de l'envoi de la commande. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black min-h-screen pt-32 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/cart')}
              className="text-gray-400 hover:text-white mb-4"
              data-testid="back-to-cart-btn"
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour au panier
            </Button>
            <h1 className="text-4xl font-bold text-white mb-2">
              Finaliser la <span className="text-red-600">Commande</span>
            </h1>
            <p className="text-gray-400">Remplissez vos informations pour recevoir votre commande</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Choix : À emporter ou Livraison */}
                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Type de commande *</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, orderType: 'emporter' }))}
                      data-testid="order-type-emporter"
                      className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all duration-200 ${
                        formData.orderType === 'emporter'
                          ? 'border-red-600 bg-red-600/20 shadow-lg shadow-red-600/20'
                          : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                      }`}
                    >
                      <ShoppingBag size={32} className={formData.orderType === 'emporter' ? 'text-red-400' : 'text-gray-400'} />
                      <span className={`font-bold text-base sm:text-lg ${formData.orderType === 'emporter' ? 'text-white' : 'text-gray-300'}`}>À emporter</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, orderType: 'livraison' }))}
                      data-testid="order-type-livraison"
                      className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all duration-200 ${
                        formData.orderType === 'livraison'
                          ? 'border-red-600 bg-red-600/20 shadow-lg shadow-red-600/20'
                          : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                      }`}
                    >
                      <Truck size={32} className={formData.orderType === 'livraison' ? 'text-red-400' : 'text-gray-400'} />
                      <span className={`font-bold text-base sm:text-lg ${formData.orderType === 'livraison' ? 'text-white' : 'text-gray-300'}`}>En livraison</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, orderType: 'programmer' }))}
                      data-testid="order-type-programmer"
                      className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all duration-200 ${
                        formData.orderType === 'programmer'
                          ? 'border-red-600 bg-red-600/20 shadow-lg shadow-red-600/20'
                          : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                      }`}
                    >
                      <Calendar size={32} className={formData.orderType === 'programmer' ? 'text-red-400' : 'text-gray-400'} />
                      <span className={`font-bold text-base sm:text-lg ${formData.orderType === 'programmer' ? 'text-white' : 'text-gray-300'}`}>Programmer</span>
                      <span className={`text-xs ${formData.orderType === 'programmer' ? 'text-red-300' : 'text-gray-500'}`}>Date & heure précises</span>
                    </button>
                  </div>
                </div>

                {formData.orderType && (
                <>
                {/* Informations personnelles */}
                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Informations Personnelles</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom" className="text-white">Nom *</Label>
                      <Input id="nom" name="nom" value={formData.nom} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Votre nom" data-testid="input-nom" />
                    </div>
                    <div>
                      <Label htmlFor="prenom" className="text-white">Prénom *</Label>
                      <Input id="prenom" name="prenom" value={formData.prenom} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Votre prénom" data-testid="input-prenom" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="telephone" className="text-white">Téléphone *</Label>
                    <Input id="telephone" name="telephone" type="tel" value={formData.telephone} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="06 12 34 56 78" data-testid="input-telephone" />
                  </div>

                  {formData.orderType === 'livraison' && (
                  <>
                  <div className="mt-4">
                    <Label htmlFor="adresse" className="text-white">Adresse de livraison *</Label>
                    <Input id="adresse" name="adresse" value={formData.adresse} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Numéro et nom de rue" data-testid="input-adresse" />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="codePostal" className="text-white">Code Postal</Label>
                      <Input id="codePostal" name="codePostal" value={formData.codePostal} onChange={handleInputChange} className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="13005" data-testid="input-code-postal" />
                      {ZONE_FRAIS_LIVRAISON.includes(formData.codePostal) && total < MINIMUM_LIVRAISON_GRATUITE && (
                        <p className="text-yellow-400 text-xs mt-1">Frais de livraison de {FRAIS_LIVRAISON.toFixed(2)}€ (commande &lt; {MINIMUM_LIVRAISON_GRATUITE}€)</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="ville" className="text-white">Ville</Label>
                      <Input id="ville" name="ville" value={formData.ville} onChange={handleInputChange} className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Marseille" data-testid="input-ville" />
                    </div>
                  </div>
                  </>
                  )}

                  {formData.orderType === 'programmer' && (
                  <>
                  <div className="mt-6 bg-red-600/10 border border-red-500/30 rounded-lg p-4">
                    <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                      <Calendar size={18} className="text-red-400" />
                      Date et heure de votre commande
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="scheduledDate" className="text-white">Date *</Label>
                        <Input
                          id="scheduledDate"
                          name="scheduledDate"
                          type="date"
                          min={new Date().toISOString().split('T')[0]}
                          value={formData.scheduledDate}
                          onChange={handleInputChange}
                          required
                          className="bg-gray-800 border-gray-700 text-white mt-2"
                          data-testid="input-scheduled-date"
                        />
                      </div>
                      <div>
                        <Label htmlFor="scheduledTime" className="text-white">Heure *</Label>
                        <Input
                          id="scheduledTime"
                          name="scheduledTime"
                          type="time"
                          value={formData.scheduledTime}
                          onChange={handleInputChange}
                          required
                          className="bg-gray-800 border-gray-700 text-white mt-2"
                          data-testid="input-scheduled-time"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label className="text-white mb-2 block">Mode de réception *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, scheduledMode: 'emporter' }))}
                        data-testid="scheduled-mode-emporter"
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          formData.scheduledMode === 'emporter'
                            ? 'border-red-600 bg-red-600/20'
                            : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                        }`}
                      >
                        <ShoppingBag size={18} className={formData.scheduledMode === 'emporter' ? 'text-red-400' : 'text-gray-400'} />
                        <span className={`font-semibold text-sm ${formData.scheduledMode === 'emporter' ? 'text-white' : 'text-gray-300'}`}>À emporter</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, scheduledMode: 'livraison' }))}
                        data-testid="scheduled-mode-livraison"
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          formData.scheduledMode === 'livraison'
                            ? 'border-red-600 bg-red-600/20'
                            : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                        }`}
                      >
                        <Truck size={18} className={formData.scheduledMode === 'livraison' ? 'text-red-400' : 'text-gray-400'} />
                        <span className={`font-semibold text-sm ${formData.scheduledMode === 'livraison' ? 'text-white' : 'text-gray-300'}`}>En livraison</span>
                      </button>
                    </div>
                  </div>

                  {formData.scheduledMode === 'livraison' && (
                  <>
                  <div className="mt-4">
                    <Label htmlFor="adresse" className="text-white">Adresse de livraison *</Label>
                    <Input id="adresse" name="adresse" value={formData.adresse} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Numéro et nom de rue" data-testid="input-adresse-prog" />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="codePostalProg" className="text-white">Code Postal</Label>
                      <Input id="codePostalProg" name="codePostal" value={formData.codePostal} onChange={handleInputChange} className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="13005" data-testid="input-code-postal-prog" />
                      {ZONE_FRAIS_LIVRAISON.includes(formData.codePostal) && total < MINIMUM_LIVRAISON_GRATUITE && (
                        <p className="text-yellow-400 text-xs mt-1">Frais de livraison de {FRAIS_LIVRAISON.toFixed(2)}€ (commande &lt; {MINIMUM_LIVRAISON_GRATUITE}€)</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="villeProg" className="text-white">Ville</Label>
                      <Input id="villeProg" name="ville" value={formData.ville} onChange={handleInputChange} className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Marseille" data-testid="input-ville-prog" />
                    </div>
                  </div>
                  </>
                  )}
                  </>
                  )}
                </div>

                {/* Mode de paiement */}
                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Mode de Paiement *</h2>

                  <div className="space-y-3">
                    {PAYMENT_METHODS.map((pm) => {
                      const Icon = pm.icon;
                      const isSelected = formData.paymentMethod === pm.value;
                      return (
                        <button
                          key={pm.value}
                          type="button"
                          onClick={() => selectPayment(pm.value)}
                          data-testid={`payment-${pm.value}`}
                          className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                            isSelected
                              ? 'border-red-600 bg-red-600/20 shadow-lg shadow-red-600/20'
                              : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                          }`}
                        >
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-red-600' : 'bg-gray-700'}`}>
                            <Icon size={24} className="text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-semibold">{pm.label}</p>
                            <p className="text-gray-400 text-sm">{pm.sublabel}</p>
                          </div>
                          {isSelected && (
                            <div className="bg-red-600 rounded-full p-1">
                              <Check size={16} className="text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {formData.paymentMethod === 'especes' && (
                    <div className="mt-6 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <Label className="text-white font-semibold mb-3 block">Avez-vous l'appoint ?</Label>
                      <Select value={formData.cashChange} onValueChange={(value) => setFormData(prev => ({ ...prev, cashChange: value }))}>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="cash-change-select">
                          <SelectValue placeholder="Sélectionnez..." />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="appoint" className="text-white">Oui, j'ai l'appoint</SelectItem>
                          <SelectItem value="20" className="text-white">Monnaie sur 20€</SelectItem>
                          <SelectItem value="30" className="text-white">Monnaie sur 30€</SelectItem>
                          <SelectItem value="40" className="text-white">Monnaie sur 40€</SelectItem>
                          <SelectItem value="50" className="text-white">Monnaie sur 50€</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg py-6"
                  data-testid="submit-order-btn"
                >
                  {loading ? 'Envoi en cours...' : `Valider la commande - ${(total + deliveryFee).toFixed(2)}€`}
                </Button>
                </>
                )}
              </form>
            </div>

            {/* Récapitulatif */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6 sticky top-32" data-testid="checkout-summary">
                <h2 className="text-2xl font-bold text-white mb-6">Récapitulatif</h2>

                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <div className="flex-1">
                        <p className="text-white font-semibold">
                          {item.customName || item.name}
                          {item.chausson && !item.name?.includes('Chausson') && ' (Chausson)'}
                        </p>
                        <p className="text-gray-400 text-xs">Quantité: {item.quantity}</p>
                        {item.note && <p className="text-yellow-400/70 text-xs italic">Note: {item.note}</p>}
                      </div>
                      <p className="text-red-400 font-semibold">{(item.price * item.quantity).toFixed(2)}€</p>
                    </div>
                  ))}
                  {freeDrink && (
                    <div className="flex justify-between text-sm">
                      <div className="flex-1">
                        <p className="text-green-400 font-semibold">{freeDrink.name}</p>
                        <p className="text-green-400/70 text-xs">OFFERTE</p>
                      </div>
                      <p className="text-green-400 font-semibold">0.00€</p>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-800 pt-4 mb-4">
                  <div className="flex justify-between text-gray-400 mb-2">
                    <span>Sous-total</span>
                    <span>{total.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between text-green-500 mb-2">
                    <span>
                      {formData.orderType === 'emporter' && 'À emporter'}
                      {formData.orderType === 'livraison' && 'Livraison'}
                      {formData.orderType === 'programmer' && `Programmée${formData.scheduledDate ? ` (${formData.scheduledDate} ${formData.scheduledTime || ''})` : ''}`}
                    </span>
                    <span>
                      {(formData.orderType === 'emporter' || (formData.orderType === 'programmer' && formData.scheduledMode === 'emporter'))
                        ? 'Sur place'
                        : (deliveryFee > 0 ? `${deliveryFee.toFixed(2)}€` : 'Gratuite')}
                    </span>
                  </div>
                  {deliveryFee > 0 && (
                    <p className="text-yellow-400/80 text-xs mb-2">
                      Frais de livraison pour le {formData.codePostal} (commande &lt; {MINIMUM_LIVRAISON_GRATUITE}€)
                    </p>
                  )}
                  <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-gray-800">
                    <span>Total</span>
                    <span className="text-red-500" data-testid="checkout-total">{(total + deliveryFee).toFixed(2)}€</span>
                  </div>
                </div>

                <div className="bg-red-600/20 border border-red-500/50 rounded-lg p-4">
                  <p className="text-red-400 text-sm text-center font-semibold">
                    Livraison en 30 minutes chrono !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
